/**
 * Segregation-of-duties conflicts.
 *
 * This is the page an auditor is shown, so it is written to be read by
 * someone who has to make a decision rather than by someone admiring a
 * dashboard. Two things drive the layout:
 *
 * A conflict nobody holds is latent; a conflict on the role everyone has is
 * live. `holders` is therefore a column, not a footnote — it is the
 * difference between "fix before go-live" and "fix now".
 *
 * A conflict that permits a compensating control is a conversation. One that
 * does not is a decision: split the role, or accept the risk in writing.
 * Those are separated rather than ranked together, because they need
 * different people in the room.
 */
import {
  Badge,
  Card,
  DataTable,
  EmptyState,
  PageHeader,
  Skeleton,
  StatCard,
} from "@/components/ui";
import { useApiQuery } from "@/lib/api/hooks";

interface RoleConflict {
  roleId: string;
  roleCode: string;
  roleName: string;
  rule: string;
  permissionA: string;
  permissionB: string;
  riskLevel: string;
  mitigationAllowed: boolean;
  holders: number;
}

interface UserConflict {
  tenantUserId: string;
  displayName: string | null;
  email: string | null;
  rule: string;
  permissionA: string;
  permissionB: string;
  riskLevel: string;
  mitigationAllowed: boolean;
}

interface Summary {
  activeRules: number;
  roleConflicts: number;
  userConflicts: number;
  unmitigable: number;
}

/**
 * The shape the api client hands back, which is the envelope's `data` — it
 * returns `body.data` and drops `meta`. The summary therefore has to travel
 * inside data; reading it from `meta` here renders four zeros, and on this
 * page zero is also what a healthy tenant looks like.
 */
interface Report {
  summary: Summary;
  roleConflicts: RoleConflict[];
  userConflicts: UserConflict[];
}

const riskTone = (risk: string): "danger" | "warning" | "default" =>
  risk === "Critical" || risk === "High"
    ? "danger"
    : risk === "Medium"
      ? "warning"
      : "default";

/** The permission pair, which is what someone has to act on. */
function Pair({ a, b }: { a: string; b: string }) {
  return (
    <span className="atlas-mono" style={{ fontSize: 12 }}>
      {a} <span className="atlas-muted">+</span> {b}
    </span>
  );
}

export function SodConflictsPage() {
  const { data, isLoading, error } = useApiQuery<Report>(
    ["governance", "sod-conflicts"],
    "/sod-conflicts",
  );

  if (isLoading) return <Skeleton rows={12} />;
  if (error) {
    return (
      <EmptyState
        title="Could not load the conflict report"
        message={error.message}
      />
    );
  }

  const roleConflicts = data?.roleConflicts ?? [];
  const userConflicts = data?.userConflicts ?? [];
  const summary = data?.summary;
  const live = roleConflicts.filter((c) => c.holders > 0).length;

  return (
    <div>
      <PageHeader
        title="Segregation of duties"
        subtitle="Where one person can complete a transaction end to end. This is the report an audit asks for."
        breadcrumb={["Governance", "Conflicts"]}
      />

      <div className="atlas-grid atlas-grid--4 mb-4">
        <StatCard label="Active rules" value={summary?.activeRules ?? 0} />
        <StatCard
          label="Role conflicts"
          value={summary?.roleConflicts ?? 0}
          meta={
            live > 0
              ? `${live} on a role somebody holds`
              : "none on a role anybody holds yet"
          }
          tone={live > 0 ? "negative" : undefined}
        />
        <StatCard label="User conflicts" value={summary?.userConflicts ?? 0} />
        <StatCard
          label="No compensating control"
          value={summary?.unmitigable ?? 0}
          meta="Split the role, or accept in writing"
          tone={(summary?.unmitigable ?? 0) > 0 ? "negative" : undefined}
        />
      </div>

      {(summary?.activeRules ?? 0) === 0 ? (
        <EmptyState
          title="No segregation-of-duties rules are active"
          message="Without rules this check passes every time and reports nothing, which reads as 'governed' while governing nothing. Provisioning installs the standard set; if it is empty here, someone has deactivated them."
        />
      ) : (
        <>
          <Card
            title={`Role conflicts (${roleConflicts.length})`}
            className="mb-4"
          >
            <p className="atlas-muted" style={{ fontSize: 13 }}>
              The role itself grants both halves of a rule, so everyone holding
              it breaches that rule. <strong>Holders</strong> is how many people
              that is today — a conflict on a role nobody holds is worth fixing
              before go-live rather than tonight.
            </p>
            {roleConflicts.length === 0 ? (
              <EmptyState title="No role grants both halves of a rule" />
            ) : (
              <DataTable<RoleConflict>
                keyField="roleId"
                rows={roleConflicts}
                columns={[
                  {
                    key: "riskLevel",
                    label: "Risk",
                    render: (r) => (
                      <Badge tone={riskTone(r.riskLevel)}>{r.riskLevel}</Badge>
                    ),
                  },
                  {
                    key: "roleCode",
                    label: "Role",
                    render: (r) => (
                      <span className="atlas-mono">{r.roleCode}</span>
                    ),
                  },
                  { key: "rule", label: "What it allows" },
                  {
                    key: "permissions",
                    label: "Permissions",
                    render: (r) => <Pair a={r.permissionA} b={r.permissionB} />,
                  },
                  {
                    key: "holders",
                    label: "Holders",
                    numeric: true,
                    render: (r) =>
                      r.holders > 0 ? (
                        <strong style={{ color: "var(--atlas-danger)" }}>
                          {r.holders}
                        </strong>
                      ) : (
                        <span className="atlas-muted">0</span>
                      ),
                  },
                  {
                    key: "mitigationAllowed",
                    label: "Compensating control",
                    render: (r) =>
                      r.mitigationAllowed ? (
                        <Badge>Permitted</Badge>
                      ) : (
                        <Badge tone="danger">Not permitted</Badge>
                      ),
                  },
                ]}
              />
            )}
          </Card>

          <Card title={`User conflicts (${userConflicts.length})`}>
            <p className="atlas-muted" style={{ fontSize: 13 }}>
              Someone has collected both halves across several roles. This is
              the kind that grows quietly as people change jobs, and it is
              usually fixed by removing a role rather than redesigning one.
            </p>
            {userConflicts.length === 0 ? (
              <EmptyState
                title="No user holds both halves of a rule"
                message="Assignment is blocked when it would create one, so this list stays empty unless a rule was added after the fact."
              />
            ) : (
              <DataTable<UserConflict>
                keyField="tenantUserId"
                rows={userConflicts}
                columns={[
                  {
                    key: "riskLevel",
                    label: "Risk",
                    render: (r) => (
                      <Badge tone={riskTone(r.riskLevel)}>{r.riskLevel}</Badge>
                    ),
                  },
                  {
                    key: "displayName",
                    label: "User",
                    render: (r) => r.displayName ?? r.email ?? "—",
                  },
                  { key: "rule", label: "What they can do" },
                  {
                    key: "permissions",
                    label: "Permissions",
                    render: (r) => <Pair a={r.permissionA} b={r.permissionB} />,
                  },
                ]}
              />
            )}
          </Card>
        </>
      )}
    </div>
  );
}
